/**
 * Today Panel Component
 * Personalized "cockpit" view showing today's practice status, streak, and next action
 * Your Dojo - not a brochure
 */

'use client';

import { memo } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/Skeleton';
import { Play, BookOpen, Flame, Calendar, ArrowRight } from 'lucide-react';
import { useActiveRitual } from '@/lib/hooks/useRitual';
import { useCurrentStreak } from '@/lib/hooks/useRitual';
import { useWeeklyMetrics } from '@/lib/hooks/useMetrics';
import { cn } from '@/lib/utils';

const STAGE_LABELS: Record<string, string> = {
  prime: 'Composure',
  learn: 'Learning',
  voice: 'Voice',
  do: 'Action',
  note: 'Reflection',
} as const;

export const TodayPanel = memo(function TodayPanel() {
  const {
    data: activeRitual,
    isLoading: isLoadingRitual,
    isError: isErrorRitual,
  } = useActiveRitual();
  const { data: streak, isLoading: isLoadingStreak, isError: isErrorStreak } = useCurrentStreak();
  const {
    data: weeklyMetrics,
    isLoading: isLoadingWeekly,
    isError: isErrorWeekly,
  } = useWeeklyMetrics();

  const currentStreak = (!isErrorStreak && streak) || 0;
  const weeklySessions = (!isErrorWeekly && weeklyMetrics?.rituals_completed) || 0;

  // Determine what to show
  const hasActiveRitual = !isErrorRitual && activeRitual && !activeRitual.ended;
  const currentStage = activeRitual?.current_stage;
  const stageLabel = currentStage ? STAGE_LABELS[currentStage] || currentStage : null;
  const stageNumber = currentStage
    ? ['prime', 'learn', 'voice', 'do', 'note'].indexOf(currentStage) + 1
    : null;

  const isLoading =
    (isLoadingRitual || isLoadingStreak || isLoadingWeekly) &&
    !isErrorRitual &&
    !isErrorStreak &&
    !isErrorWeekly;

  return (
    <Card
      className="p-4 sm:p-5 md:p-6 lg:p-8 bg-gradient-to-br from-background via-background to-primary/5 border-2 border-primary/20 shadow-sm relative overflow-hidden"
      role="region"
      aria-label="Today's practice status"
    >
      {/* Subtle signature pattern - topographic/contour lines */}
      <div
        className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, currentColor 0px, currentColor 1px, transparent 1px, transparent 20px),
            repeating-linear-gradient(90deg, currentColor 0px, currentColor 1px, transparent 1px, transparent 20px)
          `,
        }}
        aria-hidden="true"
      />

      <div className="relative space-y-4 sm:space-y-5" aria-live="polite" aria-busy={isLoading}>
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5 sm:space-y-2">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              Today&apos;s Practice
            </h2>
            {isLoadingStreak ? (
              <Skeleton variant="text" className="h-4 w-32" />
            ) : (
              currentStreak > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Flame className="h-4 w-4 text-primary" aria-hidden="true" />
                  <span>
                    <span className="font-semibold text-primary">{currentStreak}</span>
                    <span className="text-muted-foreground">-day streak</span>
                  </span>
                </div>
              )
            )}
          </div>
        </div>

        {/* Status Content */}
        {isLoading ? (
          <div className="space-y-4 py-4" aria-label="Loading practice status">
            <Skeleton variant="text" className="h-6 w-3/4" />
            <Skeleton variant="text" className="h-4 w-1/2" />
            <div className="flex gap-3">
              <Skeleton variant="rectangular" className="h-11 w-40" />
              <Skeleton variant="rectangular" className="h-11 w-32" />
            </div>
          </div>
        ) : hasActiveRitual && stageLabel && stageNumber ? (
          <div className="space-y-4 sm:space-y-5">
            <div className="space-y-1.5 sm:space-y-2">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Stage {stageNumber} of 5
              </div>
              <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-foreground">
                {stageLabel}
              </div>
              <p className="text-sm text-muted-foreground">Continue where you left off</p>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                asChild
                size="lg"
                className="w-full sm:w-auto rounded-xl shadow-md hover:shadow-lg transition-all font-semibold"
                aria-label="Continue your daily ritual"
              >
                <Link href="/ritual" className="flex items-center justify-center">
                  <Play className="h-5 w-5 mr-2" aria-hidden="true" />
                  Continue Daily Ritual
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="w-full sm:w-auto rounded-xl font-medium border-2"
                aria-label="Open your journal"
              >
                <Link href="/journal" className="flex items-center justify-center">
                  <BookOpen className="h-5 w-5 mr-2" aria-hidden="true" />
                  Open Journal
                </Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-5">
            <div className="space-y-1.5 sm:space-y-2">
              <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-foreground">
                Ready to begin
              </div>
              <p className="text-sm sm:text-base text-muted-foreground">
                {currentStreak > 0
                  ? `Keep your ${currentStreak}-day streak going`
                  : 'Start your daily practice'}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                asChild
                size="lg"
                className="w-full sm:w-auto rounded-xl shadow-md hover:shadow-lg transition-all font-semibold"
                aria-label="Start your daily ritual"
              >
                <Link href="/ritual" className="flex items-center justify-center">
                  <Play className="h-5 w-5 mr-2" aria-hidden="true" />
                  Start Daily Ritual
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="w-full sm:w-auto rounded-xl font-medium border-2"
                aria-label="Open your journal"
              >
                <Link href="/journal" className="flex items-center justify-center">
                  <BookOpen className="h-5 w-5 mr-2" aria-hidden="true" />
                  Open Journal
                </Link>
              </Button>
            </div>
          </div>
        )}

        {/* Quick Links */}
        <nav
          aria-label="Quick navigation links"
          className="flex flex-wrap items-center gap-2 sm:gap-3 pt-4 sm:pt-5 border-t border-border"
        >
          <Link
            href="/start"
            className="text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded py-1.5 px-1 -mx-1"
            aria-label="Get started guide"
          >
            Start Here
            <ArrowRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
          </Link>
          <span
            className="text-xs sm:text-sm text-muted-foreground/40 hidden sm:inline"
            aria-hidden="true"
          >
            •
          </span>
          <Link
            href="/modules"
            className="text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded py-1.5 px-1 -mx-1"
            aria-label="Browse learning modules"
          >
            Browse learning modules
            <ArrowRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
          </Link>
          <span
            className="text-xs sm:text-sm text-muted-foreground/40 hidden sm:inline"
            aria-hidden="true"
          >
            •
          </span>
          <Link
            href="/scenarios"
            className="text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded py-1.5 px-1 -mx-1"
            aria-label="Pick a practice scenario"
          >
            Pick a scenario
            <ArrowRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
          </Link>
          {weeklySessions > 0 && (
            <>
              <span
                className="text-xs sm:text-sm text-muted-foreground/40 hidden sm:inline"
                aria-hidden="true"
              >
                •
              </span>
              <Link
                href="/review"
                className="text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded py-1.5 px-1 -mx-1"
                aria-label="Review your last session"
              >
                Review last session
                <ArrowRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
              </Link>
            </>
          )}
        </nav>
      </div>
    </Card>
  );
});
